import { Box, Button, IconButton, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import React, { useRef, useState } from 'react';
import moment, { Moment } from 'moment';

interface DateNavigationProps {
    onDateChange?: (date: Date) => void;
}

export const DateNavigation: React.FC<DateNavigationProps> = ({ onDateChange }) => {
    const CONTROL_SIZE = 40;
    const [selectedDate, setSelectedDate] = useState<Moment | null>(moment().startOf('day'));
    const [openDatePicker, setOpenDatePicker] = useState(false);
    const dateButtonRef = useRef<HTMLButtonElement | null>(null);

    const getDayLabel = (date: Moment | null): string => {
        if (!date) return 'Sélectionner une date';

        const today = moment().startOf('day');
        const dateOnly = date.clone().startOf('day');

        if (dateOnly.isSame(today, 'day')) {
            return 'Aujourd\'hui';
        } else {
            return dateOnly.locale('fr').format('dddd D MMMM YYYY');
        }
    };

    const handlePreviousDay = () => {
        if (!selectedDate) return;
        const newDate = selectedDate.clone().subtract(1, 'day').startOf('day');
        setSelectedDate(newDate);
        onDateChange?.(newDate.toDate());
    };

    const handleNextDay = () => {
        if (!selectedDate) return;
        const newDate = selectedDate.clone().add(1, 'day').startOf('day');
        setSelectedDate(newDate);
        onDateChange?.(newDate.toDate());
    };

    const isToday = selectedDate ? selectedDate.clone().startOf('day').isSame(moment().startOf('day'), 'day') : false;
    const isFuture = (date: Moment | null) => date ? date.clone().startOf('day').isAfter(moment().startOf('day')) : false;

    const handleGoToToday = () => {
        const today = moment().startOf('day');
        setSelectedDate(today);
        onDateChange?.(today.toDate());
    };

    const handleDatePickerChange = (date: Moment | null) => {
        if (date) {
            const normalizedDate = date.clone().startOf('day');
            setSelectedDate(normalizedDate);
            onDateChange?.(normalizedDate.toDate());
        }
        setOpenDatePicker(false);
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 3,
            }}>

            <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                    size="medium"
                    variant="outlined"
                    color="secondary"
                    onClick={handlePreviousDay}
                    sx={{
                        borderRadius: '8px',
                        width: CONTROL_SIZE,
                        height: CONTROL_SIZE,
                    }}>
                    <i className="fi fi-br-angle-small-left" style={{ fontSize: '16px' }} />
                </IconButton>
                <IconButton
                    size="medium"
                    variant="outlined"
                    color="secondary"
                    onClick={handleNextDay}
                    disabled={isFuture(selectedDate?.clone().add(1, 'day') ?? null)}
                    sx={{
                        borderRadius: '8px',
                        width: CONTROL_SIZE,
                        height: CONTROL_SIZE,
                    }}>
                    <i className="fi fi-br-angle-small-right" style={{ fontSize: '16px' }} />
                </IconButton>
                {/* Current Date */}
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {getDayLabel(selectedDate)}
                </Typography>
            </Stack>
            <Stack gap={1} flexDirection="row" alignItems="center">
                {!isToday && (
                    <Button
                        variant="outlined"
                        color="secondary"
                        onClick={handleGoToToday}
                        sx={{
                            height: CONTROL_SIZE,
                            borderRadius: '8px',
                        }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>Aujourd'hui</Typography>
                    </Button>
                )}
                <Button
                    ref={dateButtonRef}
                    variant="outlined"
                    color="secondary"
                    onClick={() => setOpenDatePicker(true)}
                    title={getDayLabel(selectedDate)}
                    aria-label={getDayLabel(selectedDate)}
                    startIcon={<i className="fi fi-br-calendar" style={{ fontSize: '16px' }} />}
                    sx={{
                        height: CONTROL_SIZE,
                        borderRadius: '8px',
                    }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>Choisir une date</Typography>
                </Button>
            </Stack>
            <DatePicker
                value={selectedDate}
                onChange={handleDatePickerChange}
                open={openDatePicker}
                disableFuture
                onOpen={() => setOpenDatePicker(true)}
                onClose={() => setOpenDatePicker(false)}
                slotProps={{
                    textField: {
                        sx: { display: 'none' },
                    },
                    popper: {
                        anchorEl: dateButtonRef.current,
                        placement: 'bottom',
                    },
                }}
            />
        </Box>
    );
};
